import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InfoBar from '../components/InfoBar';
import StatCard from '../components/StatCard';
import { PageRevealStyles, usePageReveal } from '../components/PageReveal';
import { loadExternalScript } from '../utils/scriptLoader';
import SocialHorizontalBarChart from '../components/social/SocialHorizontalBarChart';
import SocialDonutChart from '../components/social/SocialDonutChart';
import { COLOR_PALETTE } from '../components/social/palette';
import BluePigsChart from '../components/charts/BluePigsChart';
import { SOCIAL_CSV_BY_YEAR, getCsvUrls } from '../utils/dataSources';

const D3_URL = 'https://d3js.org/d3.v7.min.js';
const SOCIAL_CSVS = getCsvUrls(SOCIAL_CSV_BY_YEAR);

const BLUE_PIGS_DATA = [
  { label: 'VM', value: 1, color: COLOR_PALETTE.VM },
  { label: 'EJ', value: 0, color: COLOR_PALETTE.EJ },
  { label: 'WBG', value: 0, color: COLOR_PALETTE.WBG },
  { label: 'W2J', value: 0, color: COLOR_PALETTE.W2J },
];

const SOCIAL_SAYINGS_MAP = {
  sando: 'Sando!',
  idiot_counter: 'Idiot',
  lets_enjoy_this: "Let's Enjoy This!",
  fire_it_in: 'Fire it in there!',
  thats_what_she_said: "That's what she said!",
  innage: 'Innage',
  was_it_over_when_the_germans: 'Was it over when the Germans?',
  now_were_loggin: "Now we're loggin'!",
  how_much_was_that: 'How much was that?',
  poke_in_the_eye: 'Better than a poke in the eye!',
  sure_get_5_a_lot: 'You sure get 5 a lot!',
  those_should_be_wild: 'Those should be wild!',
  jingles: 'Jingles',
  coins: 'Coins',
  thats_a_massive_guys: "That's a massive guys",
  baby_doll: 'Baby Doll (W2J)',
  a_lot_of_people_are_saying_it: 'A lot of people are saying it!',
  flags: 'Flags!',
  balls: 'Balls!',
  hats: 'Hats!',
};

const SOCIAL_TRENDS_MAP = {
  big_checks: 'Big Checks',
  spirit_fingers: 'Spirit Fingers',
  victory_laps: 'Victory Laps',
  triple_pops: 'Triple Pops',
  wbg_belly_laughs: 'WBG Belly Laughs',
  crickets: 'Crickets',
  bonu_in_a_bonu: 'Bonu in a Bonu',
  first_spin_johnson: 'First Spin Johnson',
  next_spin_johnson: 'Next Spin Johnson',
  accidental_fast_spins: 'Accidental Fast Spins',
  vm_wears_chain: 'VM Wears Chain',
  ej_remembers_to_add_content: 'EJ Remembers to Add Content',
  blazed_it_and_420: 'Blazed it / 420',
  vm_story_triggers_a_bonus: 'VM Story Triggers a Bonus',
  wbg_pun_counter: 'WBG Puns',
  double_pops: 'Double Pops',
  bbbs: 'Big Beautiful Buffalo Spins',
};

const parseNumeric = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    if (!cleaned) return 0;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const mergeSocialRows = (rows) =>
  rows.reduce((acc, row) => {
    Object.entries(row).forEach(([key, value]) => {
      if (!key) return;
      const numeric = parseNumeric(value);
      const current = parseNumeric(acc[key]);
      if (Number.isFinite(numeric)) {
        acc[key] = (Number.isFinite(current) ? current : 0) + numeric;
      }
    });
    return acc;
  }, {});

const getSocialUrls = (selectedYear) => {
  if (selectedYear == null) return SOCIAL_CSVS;
  const url = SOCIAL_CSV_BY_YEAR[selectedYear];
  return url ? [url] : [];
};

const formatCurrency = (value) => {
  if (!Number.isFinite(value)) return '—';
  const absolute = Math.abs(value);
  const formatted = absolute.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
};

const formatCountString = (value) => {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const formatPercentString = (value) => {
  if (!Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}%`;
};

const buildChartData = (row, mapping) => {
  if (!row) return [];
  return Object.keys(mapping)
    .map((key) => ({ label: mapping[key], value: parseNumeric(row[key]) }))
    .filter((entry) => entry.value > 0)
    .sort((a, b) => b.value - a.value);
};

const SocialPage = ({ isActive, selectedYear }) => {
  const rootRef = useRef(null);
  usePageReveal(
    rootRef,
    isActive,
    '.stats-container, .stats-container-three, .chart-container, .extra-stats-container, .chart-card, .vm-infobar-wrap',
  );

  const [d3Lib, setD3Lib] = useState(null);
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isActive) return undefined;

    let cancelled = false;

    async function loadData() {
      setLoading(true);
      try {
        const d3 = await loadExternalScript(D3_URL, 'd3');
        if (cancelled) return;
        setD3Lib(d3);

        const urls = getSocialUrls(selectedYear);
        if (!urls.length) {
          setRow(null);
          setError(new Error('No social data available for the selected year.'));
          return;
        }

        const socialSets = await Promise.all(urls.map((url) => d3.csv(url)));
        if (cancelled) return;

        const rows = socialSets.map((set) => set?.[0]).filter(Boolean);
        if (!rows.length) {
          setRow(null);
          setError(new Error('No social data available.'));
          return;
        }

        const mergedRow = selectedYear == null && rows.length > 1 ? mergeSocialRows(rows) : rows[0];
        setRow(mergedRow ?? null);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to load social stats.'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [isActive, selectedYear]);

  const formatCount = useCallback((value) => formatCountString(Number(value) || 0), []);

  const sayingsData = useMemo(() => buildChartData(row, SOCIAL_SAYINGS_MAP).slice(0, 15), [row]);
  const trendsData = useMemo(() => buildChartData(row, SOCIAL_TRENDS_MAP), [row]);

  const statsSummary = useMemo(() => {
    if (!row) {
      return {
        bigCheckAmount: { text: '—', tone: 'neutral' },
        bigChecks: { text: '—', tone: 'neutral' },
        triplePops: { text: '—', tone: 'neutral' },
        oneTicketersSlot: { text: '—', tone: 'neutral' },
      };
    }

    const bigCheckAmountValue = parseNumeric(row.big_check_amount);
    const bigChecksValue = parseNumeric(row.big_checks);
    const triplePopsValue = parseNumeric(row.triple_pops);
    const oneTicketersValue = parseNumeric(row.one_ticketers_slot);

    return {
      bigCheckAmount: { text: formatCurrency(bigCheckAmountValue), tone: bigCheckAmountValue >= 0 ? 'positive' : 'negative' },
      bigChecks: { text: formatCountString(bigChecksValue), tone: 'positive' },
      triplePops: { text: formatCountString(triplePopsValue), tone: 'positive' },
      oneTicketersSlot: { text: formatCountString(oneTicketersValue), tone: 'positive' },
    };
  }, [row]);

  const gruntStats = useMemo(() => {
    const total = parseNumeric(row?.ej_grunts_total);
    const successful = parseNumeric(row?.ej_grunt_bonus);
    const unsuccessful = Math.max(total - successful, 0);
    const successPct = total > 0 ? (successful / total) * 100 : 0;
    const successTone = successPct >= 50 ? 'positive' : successPct === 0 ? 'negative' : 'neutral';
    return {
      total,
      successful,
      unsuccessful,
      successPct,
      successTone,
    };
  }, [row]);

  const gruntDonutData = useMemo(
    () => [
      { label: 'Successful', value: gruntStats.successful, color: COLOR_PALETTE.VM },
      { label: 'Unsuccessful', value: gruntStats.unsuccessful, color: COLOR_PALETTE.WBG },
    ].filter((entry) => entry.value > 0),
    [gruntStats.successful, gruntStats.unsuccessful],
  );

  if (!isActive) return null;

  return (
    <section className="app-page" ref={rootRef}>
      <PageRevealStyles />

      <div className="stats-container-three">
        <div className="stats-card-image">
          <img src="/images/W2J.png" alt="W2J" />
        </div>
        <div className="stats-card-text">
            <p>
            There's only one stat that really matters. Has anybody ever got a <strong>FULL SCREEN OF BLUE PIGS</strong>?
        </p>
        </div>
      </div>

      <div className="vpp-section-container">
        <BluePigsChart data={BLUE_PIGS_DATA} />
        <div className="stats-column">
          <StatCard title="Bet Amount" value={formatCurrency(25)} tone="positive" />
          <StatCard title="Win Amount" value={formatCurrency(4000)} tone="positive" />
        </div>
      </div>

      <div className="stats-container-three">
        <div className="stats-card-image">
          <img src="/images/WBG.png" alt="WBG" />
        </div>
        <div className="stats-card-text">
        <p>
            The Discord stats team has it all covered—tracking every mention of <strong>SANDO</strong>, every enthusiastic
            <strong> FIRE IT IN THERE</strong>, and every heartfelt <strong>LET'S ENJOY THIS</strong>.
   </p>
        </div>
      </div>

      {loading ? (
        <InfoBar type="info" title="Loading social stats" animate>
          Hang tight while we grab the latest numbers from the stats bunker.
        </InfoBar>
      ) : null}

      {error ? (
        <InfoBar type="danger" title="Unable to load social stats" animate>
          {error.message}
        </InfoBar>
      ) : null}

      <SocialHorizontalBarChart
        d3={d3Lib}
        data={sayingsData}
        title="SOCIAL SAYINGS"
        valueFormatter={formatCount}
      />

      <SocialHorizontalBarChart
        d3={d3Lib}
        data={trendsData}
        title="TRENDS"
        valueFormatter={formatCount}
      />

      <div className="stats-container">
        <StatCard title="Big Check Amount" value={statsSummary.bigCheckAmount.text} tone={statsSummary.bigCheckAmount.tone} />
        <StatCard title="Big Checks" value={statsSummary.bigChecks.text} tone={statsSummary.bigChecks.tone} />
        <StatCard title="Triple Pops" value={statsSummary.triplePops.text} tone={statsSummary.triplePops.tone} />
        <StatCard
          title="One Ticketers (Slot)"
          value={statsSummary.oneTicketersSlot.text}
          tone={statsSummary.oneTicketersSlot.tone}
        />
      </div>

      <div className="stats-container-three">
        <div className="stats-card-image">
          <img src="/images/EJ.png" alt="EJ" />
        </div>
        <div className="stats-card-text">
        <p>
            <strong>URH, URGHH, UGHHHH, UNGH, URRNN, UFFF, URRGH, UHHNN, URRHH, UHHMM.</strong> We all love EJ's grunts. But how
            often does a bonus follow the hype?
</p>
        </div>
      </div>

      <div className="extra-stats-container">
        <div className="stats-column">
          <StatCard title="Total EJ Grunts" value={formatCountString(gruntStats.total)} tone="positive" />
          <StatCard
            title="Grunt Success Percentage"
            value={formatPercentString(gruntStats.successPct)}
            tone={gruntStats.successTone}
          />
        </div>
        <SocialDonutChart d3={d3Lib} data={gruntDonutData} title="EJ GRUNTS" />
        <div className="stats-column">
          <StatCard title="Successful Grunts" value={formatCountString(gruntStats.successful)} tone="positive" />
          <StatCard title="Unsuccessful Grunts" value={formatCountString(gruntStats.unsuccessful)} tone="negative" />
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '2rem', padding: '1rem 0', borderTop: '1px solid #ddd', fontSize: '0.9rem', color: '#444' }}>
        Image credit for Vegas Matt and Team artwork is to{' '}
        <a href="https://x.com/jessediebolt?s=21" target="_blank" rel="noreferrer" style={{ color: '#76ace0', textDecoration: 'none' }}>
          @JESSEDIEBOLT
        </a>
        . Thank you!
      </div>
      <div style={{ textAlign: 'center', fontSize: '0.9rem', color: '#444' }}>
        For enquiries, please contact:{' '}
        <a href="mailto:info@officialvegasmattstats.com" target="_blank" rel="noreferrer" style={{ color: '#76ace0', textDecoration: 'none' }}>
          info@unofficialvegasmattstats.com
        </a>
        .
      </div>
    </section>
  );
};

export default SocialPage;
